import React, { useState } from "react";
import dayjs from "dayjs";
import {
  ConfigProvider,
  DatePicker,
  TimePicker,
  theme as antTheme,
} from "antd";
import {
  Plus,
  ClipboardCheck,
  SlidersHorizontal,
  DollarSign,
  Save,
  RotateCcw,
  X,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const RuleConfigurations = () => {
  const { colors, theme } = useTheme();

  const isLightTheme = theme === "white" || theme === "light";

  const [isAddRuleModalOpen, setIsAddRuleModalOpen] = useState(false);

  const [newRuleData, setNewRuleData] = useState({
    ruleName: "",
    districtScope: "FB151",
    flowRate: "350",
    minimumDuration: "30",

    evenDays: ["T", "F"],
    evenStartTime: "06:00 PM",
    evenEndTime: "10:00 AM",

    oddDays: ["M", "W", "T2"],
    oddStartTime: "06:00 PM",
    oddEndTime: "10:00 AM",
  });

  const [formData, setFormData] = useState({
    flowRate: "350",
    duration: "30",
    warningThreshold: "250",
    violationThreshold: "350",
    residential: "75",
    commercial: "250",
    builder: "500",
    agricultural: "125",
    secondOffense: "2.0",
    thirdOffense: "5.0",

    // New fields
    noWateringEnabled: false,
    restrictedStart: "10:00",
    restrictedEnd: "18:00",
    activeDays: ["M", "T", "W", "T2", "F"],
    gracePeriod: "3",
    resetPeriod: dayjs().format("YYYY-MM-DD"),
  });

  const rules = [
    {
      name: "Stage 2 Restrictions",
      status: "ACTIVE",
      date: "May 15, 2024",
      color: "cyan",
    },
    {
      name: "Summer Peak Baseline",
      status: "INACTIVE",
      date: "June 01, 2024",
      color: "slate",
    },
    {
      name: "Critical Emergency Flow",
      status: "ACTIVE",
      date: "Immediate",
      color: "red",
    },
  ];

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleNewRuleChange = (key, value) => {
    setNewRuleData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleParityDay = (type, dayKey) => {
    const fieldName = type === "even" ? "evenDays" : "oddDays";

    setNewRuleData((prev) => {
      const alreadySelected = prev[fieldName].includes(dayKey);

      return {
        ...prev,
        [fieldName]: alreadySelected
          ? prev[fieldName].filter((day) => day !== dayKey)
          : [...prev[fieldName], dayKey],
      };
    });
  };

  const toggleActiveDay = (dayKey) => {
    setFormData((prev) => {
      const alreadySelected = prev.activeDays.includes(dayKey);

      return {
        ...prev,
        activeDays: alreadySelected
          ? prev.activeDays.filter((day) => day !== dayKey)
          : [...prev.activeDays, dayKey],
      };
    });
  };

  const cardStyle = {
    backgroundColor: colors.cardBg,
    borderColor: colors.border,
  };

  const inputStyle = {
    backgroundColor: colors.background,
    borderColor: colors.border,
    color: colors.text,
  };

  const labelClass =
    "mb-2 block text-xs font-extrabold uppercase tracking-wide text-sky-100/85";

  const inputClass =
    "w-full rounded-lg border px-4 py-3 text-sm font-medium outline-none transition-all focus:ring-2";

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: colors.background,
        color: colors.text,
      }}
    >
      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1
            className="bg-clip-text text-4xl font-bold text-transparent"
            style={{
              backgroundImage: `linear-gradient(to right, ${colors.primary}, ${
                colors.secondary || colors.primary
              })`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              lineHeight: "normal",
            }}
          >
            Drought Rule Configuration
          </h1>

          <p className="mt-2 text-sm" style={{ color: colors.textSecondary }}>
            Establish and manage water conservation parameters for the current
            season.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 rounded-lg border px-5 py-3 text-sm font-bold transition-all hover:opacity-90"
            style={{
              backgroundColor: colors.cardBg,
              borderColor: colors.border,
              color: colors.text,
            }}
          >
            <ClipboardCheck size={18} />
            Manage Address Exemptions
          </button>

          <button
            type="button"
            onClick={() => setIsAddRuleModalOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-extrabold transition-all hover:opacity-90"
            style={{
              backgroundColor: colors.primary,
              color: colors.buttonText || "#061727",
            }}
          >
            <Plus size={18} />
            Add New Rule
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_510px]">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Active Rules List */}
          <div
            className="overflow-hidden rounded-lg border shadow-xl"
            style={cardStyle}
          >
            <div
              className="flex items-center justify-between border-b px-4 py-5"
              style={{ borderColor: colors.border }}
            >
              <h3
                className="text-sm font-extrabold uppercase tracking-wide"
                style={{ color: colors.primary }}
              >
                Active Rules List
              </h3>

              <SlidersHorizontal size={19} style={{ color: colors.text }} />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead>
                  <tr
                    className="border-b text-left"
                    style={{ borderColor: colors.border }}
                  >
                    <th className="px-6 py-5 text-xs font-extrabold uppercase tracking-wider text-sky-100/80">
                      Rule Name
                    </th>
                    <th className="px-6 py-5 text-xs font-extrabold uppercase tracking-wider text-sky-100/80">
                      Status
                    </th>
                    <th className="px-6 py-5 text-xs font-extrabold uppercase tracking-wider text-sky-100/80">
                      Effective Date
                    </th>
                    <th className="px-6 py-5 text-xs font-extrabold uppercase tracking-wider text-sky-100/80">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {rules.map((rule, index) => (
                    <tr
                      key={rule.name}
                      className="border-b last:border-b-0"
                      style={{
                        borderColor: `${colors.border}80`,
                        backgroundColor:
                          index === 0 ? "rgba(148, 163, 184, 0.03)" : "none",
                      }}
                    >
                      <td className="px-6 py-6 text-sm font-semibold">
                        {rule.name}
                      </td>

                      <td className="px-6 py-6">
                        <span
                          className={`rounded-full px-3 py-1 text-[11px] font-extrabold ${
                            rule.color === "cyan"
                              ? "bg-cyan-400 text-slate-950"
                              : rule.color === "red"
                                ? "bg-red-700 text-white"
                                : "bg-slate-600/60 text-slate-100"
                          }`}
                        >
                          {rule.status}
                        </span>
                      </td>

                      <td className="px-6 py-6 text-sm text-sky-100/90">
                        {rule.date}
                      </td>

                      <td className="px-6 py-6">
                        <button
                          type="button"
                          className="text-sm font-extrabold hover:underline"
                          style={{ color: colors.primary }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Configuration Card */}
          <div
            className="overflow-hidden rounded-lg border shadow-xl mb-5"
            style={cardStyle}
          >
            <div
              className="border-b px-4 py-5"
              style={{ borderColor: colors.border }}
            >
              <h3
                className="text-sm font-extrabold uppercase tracking-wide"
                style={{ color: colors.primary }}
              >
                Configuration: Stage 2 Restrictions
              </h3>
            </div>

            <div className="space-y-8 p-6">
              {/* District Scope */}

              {/* Irrigation Threshold */}
              {/* <div>
                <div className="mb-6 flex items-center gap-4">
                  <div
                    className="h-8 w-1"
                    style={{ backgroundColor: colors.primary }}
                  />

                  <h2 className="text-xl font-extrabold text-sky-50">
                    Irrigation Detection Thresholds
                  </h2>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className={labelClass}>Flow Rate (GPH)</label>

                    <input
                      value={formData.flowRate}
                      onChange={(e) =>
                        handleChange("flowRate", e.target.value)
                      }
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Minimum Duration (Minutes)
                    </label>

                    <input
                      value={formData.duration}
                      onChange={(e) =>
                        handleChange("duration", e.target.value)
                      }
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div> */}

              {/* Warning & Violation */}
              {/* <div
                className="border-t pt-8"
                style={{ borderColor: `${colors.border}90` }}
              >
                <div className="mb-6 flex items-center gap-4">
                  <div
                    className="h-8 w-1"
                    style={{ backgroundColor: colors.primary }}
                  />

                  <h2 className="text-xl font-extrabold text-sky-50">
                    Warning & Violation Rules
                  </h2>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className={labelClass}>Warning Threshold</label>

                    <input
                      value={formData.warningThreshold}
                      onChange={(e) =>
                        handleChange("warningThreshold", e.target.value)
                      }
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Violation Threshold</label>

                    <input
                      value={formData.violationThreshold}
                      onChange={(e) =>
                        handleChange("violationThreshold", e.target.value)
                      }
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div> */}

              {/* Global No-Watering Windows */}
              <div
                className="border-t pt-8"
                style={{ borderColor: `${colors.border}90` }}
              >
                <div className="mb-6 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div
                      className="h-8 w-1"
                      style={{ backgroundColor: colors.primary }}
                    />

                    <h2 className="text-xl font-extrabold text-sky-50">
                      Global No-Watering Windows
                    </h2>
                  </div>

                  {/* <button
                    type="button"
                    onClick={() =>
                      handleChange(
                        "noWateringEnabled",
                        !formData.noWateringEnabled,
                      )
                    }
                    className="relative h-5 w-9 rounded-full transition-all"
                    style={{
                      backgroundColor: formData.noWateringEnabled
                        ? colors.primary
                        : `${colors.border}cc`,
                    }}
                  >
                    <span
                      className="absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full bg-white transition-all"
                      style={{
                        left: formData.noWateringEnabled ? "20px" : "3px",
                      }}
                    />
                  </button> */}
                </div>

                <div
                  className="rounded-xl border p-6"
                  style={{
                    backgroundColor: colors.cardBg,
                    borderColor: colors.border,
                  }}
                >
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                      <label className={labelClass}>
                        Daily Restricted Start
                      </label>

                      <div
                        className="flex h-[48px] items-center rounded-lg border px-3"
                        style={inputStyle}
                      >
                        <ConfigProvider
                          theme={{
                            algorithm: isLightTheme
                              ? antTheme.defaultAlgorithm
                              : antTheme.darkAlgorithm,
                            token: {
                              colorPrimary: colors.primary,
                              colorBgContainer: colors.background,
                              colorText: colors.text,
                              colorTextPlaceholder: colors.textSecondary,
                              colorBorder: "transparent",
                              controlHeight: 40,
                              borderRadius: 8,
                            },
                          }}
                        >
                          <TimePicker
                            value={
                              formData.restrictedStart
                                ? dayjs(formData.restrictedStart, "HH:mm")
                                : null
                            }
                            onChange={(time, timeString) => {
                              handleChange("restrictedStart", timeString);
                            }}
                            format="hh:mm A"
                            use12Hours
                            showNow={false}
                            minuteStep={5}
                            variant="borderless"
                            className="w-full cursor-pointer"
                            style={{
                              background: "transparent",
                              color: colors.text,
                            }}
                            placeholder="Select start time"
                          />
                        </ConfigProvider>
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>Daily Restricted End</label>

                      <div
                        className="flex h-[48px] items-center rounded-lg border px-3"
                        style={inputStyle}
                      >
                        <ConfigProvider
                          theme={{
                            algorithm: isLightTheme
                              ? antTheme.defaultAlgorithm
                              : antTheme.darkAlgorithm,
                            token: {
                              colorPrimary: colors.primary,
                              colorBgContainer: colors.background,
                              colorText: colors.text,
                              colorTextPlaceholder: colors.textSecondary,
                              colorBorder: "transparent",
                              controlHeight: 40,
                              borderRadius: 8,
                            },
                          }}
                        >
                          <TimePicker
                            value={
                              formData.restrictedEnd
                                ? dayjs(formData.restrictedEnd, "HH:mm")
                                : null
                            }
                            onChange={(time, timeString) => {
                              handleChange("restrictedEnd", timeString);
                            }}
                            format="hh:mm A"
                            use12Hours
                            showNow={false}
                            minuteStep={5}
                            variant="borderless"
                            className="w-full cursor-pointer"
                            style={{
                              background: "transparent",
                              color: colors.text,
                            }}
                            placeholder="Select end time"
                          />
                        </ConfigProvider>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className={labelClass}>Active Days</label>

                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: "M", label: "M" },
                        { key: "T", label: "T" },
                        { key: "W", label: "W" },
                        { key: "T2", label: "T" },
                        { key: "F", label: "F" },
                        { key: "S", label: "S" },
                        { key: "S2", label: "S" },
                      ].map((day) => {
                        const isActive = formData.activeDays.includes(day.key);

                        return (
                          <button
                            key={day.key}
                            type="button"
                            onClick={() => toggleActiveDay(day.key)}
                            className="h-9 w-9 rounded-md border text-xs font-extrabold transition-all"
                            style={{
                              backgroundColor: isActive
                                ? colors.primary
                                : "transparent",
                              borderColor: isActive
                                ? colors.primary
                                : colors.border,
                              color: isActive
                                ? colors.buttonText || "#061727"
                                : colors.text,
                            }}
                          >
                            {day.label}
                          </button>
                        );
                      })}
                    </div>

                    <p
                      className="mt-4 text-xs italic"
                      style={{ color: colors.textSecondary }}
                    >
                      Overrides all other irrigation schedules for all address
                      types during this period.
                    </p>

                    <div
                      className="mt-6 flex justify-end border-t pt-5"
                      style={{ borderColor: `${colors.border}90` }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          console.log("Global No-Watering Updated:", {
                            noWateringEnabled: formData.noWateringEnabled,
                            restrictedStart: formData.restrictedStart,
                            restrictedEnd: formData.restrictedEnd,
                            activeDays: formData.activeDays,
                          });
                        }}
                        className="inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-extrabold transition-all hover:opacity-90"
                        style={{
                          backgroundColor: colors.primary,
                          color: colors.buttonText || "#061727",
                        }}
                      >
                        Update
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Violation Escalation Logic */}
              <div
                className="border-t pt-8"
                style={{ borderColor: `${colors.border}90` }}
              >
                <div className="mb-6 flex items-center gap-4">
                  <div
                    className="h-8 w-1"
                    style={{ backgroundColor: colors.primary }}
                  />

                  <h2 className="text-xl font-extrabold text-sky-50">
                    Violation Escalation Logic
                  </h2>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className={labelClass}>
                      Grace Period (Warnings Only)
                    </label>

                    <div className="flex items-center gap-3">
                      <input
                        value={formData.gracePeriod}
                        onChange={(e) =>
                          handleChange("gracePeriod", e.target.value)
                        }
                        className="w-20 rounded-lg border px-4 py-3 text-center text-sm font-bold outline-none"
                        style={inputStyle}
                      />

                      <span className="text-sm font-semibold text-sky-50">
                        First N offenses are warnings
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Reset Period</label>

                    <div
                      className="flex h-[48px] items-center rounded-lg border px-3"
                      style={inputStyle}
                    >
                      <ConfigProvider
                        theme={{
                          algorithm: isLightTheme
                            ? antTheme.defaultAlgorithm
                            : antTheme.darkAlgorithm,
                          token: {
                            colorPrimary: colors.primary,
                            colorBgContainer: colors.background,
                            colorText: colors.text,
                            colorTextPlaceholder: colors.textSecondary,
                            colorBorder: "transparent",
                            controlHeight: 40,
                            borderRadius: 8,
                          },
                        }}
                      >
                        <DatePicker
                          value={
                            formData.resetPeriod
                              ? dayjs(formData.resetPeriod, "YYYY-MM-DD")
                              : null
                          }
                          onChange={(date, dateString) => {
                            handleChange("resetPeriod", dateString);
                          }}
                          format="YYYY-MM-DD"
                          placeholder="Select reset date"
                          variant="borderless"
                          className="w-full cursor-pointer"
                          style={{
                            background: "transparent",
                            color: colors.text,
                          }}
                          disabledDate={(current) =>
                            current && current < dayjs().startOf("day")
                          }
                        />
                      </ConfigProvider>
                    </div>
                  </div>
                </div>

                <div
                  className="mt-6 flex justify-end border-t pt-5"
                  style={{ borderColor: `${colors.border}90` }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      console.log("Violation Escalation Updated:", {
                        gracePeriod: formData.gracePeriod,
                        resetPeriod: formData.resetPeriod,
                      });
                    }}
                    className="inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-extrabold transition-all hover:opacity-90"
                    style={{
                      backgroundColor: colors.primary,
                      color: colors.buttonText || "#061727",
                    }}
                  >
                    Update
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              {/* <div
                className="flex flex-col gap-3 border-t pt-6 sm:flex-row sm:justify-end"
                style={{ borderColor: `${colors.border}90` }}
              >
                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border px-5 py-3 text-sm font-bold transition-all hover:opacity-90"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                    color: colors.text,
                  }}
                >
                  <RotateCcw size={17} />
                  Reset
                </button>

                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-extrabold transition-all hover:opacity-90"
                  style={{
                    backgroundColor: colors.primary,
                    color: colors.buttonText || "#061727",
                  }}
                >
                  <Save size={17} />
                  Save Configuration
                </button>
              </div> */}
            </div>
          </div>
        </div>

        {/* Right Penalty Structure */}
        <div
          className="h-fit overflow-hidden rounded-lg border shadow-xl"
          style={cardStyle}
        >
          <div
            className="border-b px-4 py-5"
            style={{ borderColor: colors.border }}
          >
            <h3
              className="text-sm font-extrabold uppercase tracking-wide"
              style={{ color: colors.primary }}
            >
              Penalty Structure by Rate Code
            </h3>
          </div>

          <div className="p-6">
            <div className="space-y-4">
              {[
                ["Residential", "residential"],
                ["Commercial", "commercial"],
                ["Builder", "builder"],
                ["Agricultural", "agricultural"],
              ].map(([label, key]) => (
                <div
                  key={key}
                  className="flex items-center justify-between gap-4"
                >
                  <label className="text-sm font-extrabold text-sky-50">
                    {label}
                  </label>

                  <div
                    className="flex overflow-hidden rounded-lg border"
                    style={{
                      borderColor: colors.border,
                      backgroundColor: colors.background,
                    }}
                  >
                    <div
                      className="flex w-10 items-center justify-center border-r"
                      style={{
                        backgroundColor: `${colors.border}80`,
                        borderColor: colors.border,
                      }}
                    >
                      <DollarSign size={14} className="text-sky-100" />
                    </div>

                    <input
                      value={formData[key]}
                      onChange={(e) => handleChange(key, e.target.value)}
                      className="w-20 bg-transparent px-4 py-3 text-right text-sm font-bold outline-none"
                      style={{ color: colors.text }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div
              className="mt-6 border-t pt-5"
              style={{ borderColor: `${colors.border}90` }}
            >
              <button
                type="button"
                onClick={() => {
                  console.log("Penalty Structure Updated:", {
                    residential: formData.residential,
                    commercial: formData.commercial,
                    builder: formData.builder,
                    agricultural: formData.agricultural,
                  });
                }}
                className="w-full rounded-lg px-5 py-3 text-sm font-extrabold transition-all hover:opacity-90"
                style={{
                  backgroundColor: colors.primary,
                  color: colors.buttonText || "#061727",
                }}
              >
                Update
              </button>
            </div>

            {/* <div
              className="my-6 border-t"
              style={{ borderColor: `${colors.border}90` }}
            /> */}

            {/* <h4 className="mb-4 text-xs font-extrabold uppercase tracking-wide text-sky-100/80">
              Offense Tier Multiplier
            </h4>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div
                className="rounded-xl border px-5 py-5 text-center"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                }}
              >
                <p className="text-[10px] font-extrabold uppercase text-sky-100">
                  2nd Offense
                </p>

                <p
                  className="mt-1 text-3xl font-extrabold"
                  style={{ color: colors.primary }}
                >
                  {formData.secondOffense}x
                </p>
              </div>

              <div
                className="rounded-xl border px-5 py-5 text-center"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                }}
              >
                <p className="text-[10px] font-extrabold uppercase text-sky-100">
                  3rd Offense
                </p>

                <p
                  className="mt-1 text-3xl font-extrabold"
                  style={{ color: colors.primary }}
                >
                  {formData.thirdOffense}x
                </p>
              </div>
            </div> */}

            {/* <div
              className="mt-6 rounded-xl border p-4"
              style={{
                backgroundColor: `${colors.background}99`,
                borderColor: colors.border,
              }}
            >
              <p
                className="text-xs leading-6"
                style={{ color: colors.textSecondary }}
              >
                Penalty values can be configured based on customer rate code.
                Repeat offenses will apply the multiplier values shown above.
              </p>
            </div> */}
          </div>
        </div>
      </div>
      {isAddRuleModalOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 px-4 py-6">
          <div
            className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl border shadow-2xl"
            style={{
              backgroundColor: colors.background,
              borderColor: colors.border,
              color: colors.text,
            }}
          >
            {/* Modal Header */}
            <div
              className="flex items-center justify-between border-b px-6 py-5"
              style={{ borderColor: colors.border }}
            >
              <div>
                <h2 className="text-xl font-extrabold text-sky-50">
                  Add New Drought Rule
                </h2>
                <p
                  className="mt-1 text-xs"
                  style={{ color: colors.textSecondary }}
                >
                  Configure district scope, irrigation threshold, and address
                  parity schedule.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsAddRuleModalOpen(false)}
                className="rounded-lg border p-2 transition-all hover:opacity-80"
                style={{
                  borderColor: colors.border,
                  backgroundColor: colors.background,
                  color: colors.text,
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="space-y-8 p-6">
              {/* Primary District Scope */}
              <div>
                <label className={labelClass}>Rule Name</label>

                <input
                  value={newRuleData.ruleName}
                  onChange={(e) =>
                    handleNewRuleChange("ruleName", e.target.value)
                  }
                  placeholder="Enter rule name"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              {/* Irrigation Detection Thresholds */}
              <div>
                <div className="mb-6 flex items-center gap-4">
                  <div
                    className="h-8 w-1"
                    style={{ backgroundColor: colors.primary }}
                  />

                  <h2 className="text-xl font-extrabold text-sky-50">
                    Irrigation Detection Thresholds
                  </h2>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className={labelClass}>Flow Rate (GPH)</label>

                    <input
                      type="number"
                      value={newRuleData.flowRate}
                      onChange={(e) =>
                        handleNewRuleChange("flowRate", e.target.value)
                      }
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Minimum Duration (Minutes)
                    </label>

                    <input
                      type="number"
                      value={newRuleData.minimumDuration}
                      onChange={(e) =>
                        handleNewRuleChange("minimumDuration", e.target.value)
                      }
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              {/* Address Parity Scheduling */}
              <div
                className="border-t pt-8"
                style={{ borderColor: `${colors.border}90` }}
              >
                <div className="mb-6 flex items-center gap-4">
                  <div
                    className="h-8 w-1"
                    style={{ backgroundColor: colors.primary }}
                  />

                  <h2 className="text-xl font-extrabold text-sky-50">
                    Address Parity Scheduling
                  </h2>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {/* Even Addresses */}
                  <div
                    className="rounded-xl border p-6"
                    style={{
                      backgroundColor: colors.cardBg,
                      borderColor: colors.border,
                    }}
                  >
                    <div className="mb-5 flex items-center justify-between gap-3">
                      <h3
                        className="text-sm font-extrabold uppercase tracking-wide"
                        style={{ color: colors.primary }}
                      >
                        Even Addresses
                      </h3>

                      <p className="text-xs font-medium text-sky-100/80">
                        Ends in 0, 2, 4, 6, 8
                      </p>
                    </div>

                    <label className={labelClass}>Permitted Days</label>

                    <div className="mb-5 flex flex-wrap gap-2">
                      {[
                        { key: "M", label: "M" },
                        { key: "T", label: "T" },
                        { key: "W", label: "W" },
                        { key: "T2", label: "T" },
                        { key: "F", label: "F" },
                        { key: "S", label: "S" },
                        { key: "S2", label: "S" },
                      ].map((day) => {
                        const isActive = newRuleData.evenDays.includes(day.key);

                        return (
                          <button
                            key={day.key}
                            type="button"
                            onClick={() => toggleParityDay("even", day.key)}
                            className="h-9 w-9 rounded-md border text-xs font-extrabold transition-all"
                            style={{
                              backgroundColor: isActive
                                ? colors.primary
                                : "transparent",
                              borderColor: isActive
                                ? colors.primary
                                : colors.border,
                              color: isActive
                                ? colors.buttonText || "#061727"
                                : colors.text,
                            }}
                          >
                            {day.label}
                          </button>
                        );
                      })}
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className={labelClass}>Start Time</label>

                        <div
                          className="flex h-[48px] items-center rounded-lg border px-3"
                          style={inputStyle}
                        >
                          <ConfigProvider
                            theme={{
                              algorithm: isLightTheme
                                ? antTheme.defaultAlgorithm
                                : antTheme.darkAlgorithm,
                              token: {
                                colorPrimary: colors.primary,
                                colorBgContainer: colors.background,
                                colorText: colors.text,
                                colorTextPlaceholder: colors.textSecondary,
                                colorBorder: "transparent",
                                controlHeight: 40,
                                borderRadius: 8,
                              },
                            }}
                          >
                            <TimePicker
                              value={
                                newRuleData.evenStartTime
                                  ? dayjs(newRuleData.evenStartTime, "hh:mm A")
                                  : null
                              }
                              onChange={(time, timeString) => {
                                handleNewRuleChange(
                                  "evenStartTime",
                                  timeString,
                                );
                              }}
                              format="hh:mm A"
                              use12Hours
                              minuteStep={5}
                              showNow={false}
                              variant="borderless"
                              className="w-full cursor-pointer"
                              style={{
                                background: "transparent",
                                color: colors.text,
                              }}
                              placeholder="Select start time"
                            />
                          </ConfigProvider>
                        </div>
                      </div>

                      <div>
                        <label className={labelClass}>End Time</label>

                        <div
                          className="flex h-[48px] items-center rounded-lg border px-3"
                          style={inputStyle}
                        >
                          <ConfigProvider
                            theme={{
                              algorithm: isLightTheme
                                ? antTheme.defaultAlgorithm
                                : antTheme.darkAlgorithm,
                              token: {
                                colorPrimary: colors.primary,
                                colorBgContainer: colors.background,
                                colorText: colors.text,
                                colorTextPlaceholder: colors.textSecondary,
                                colorBorder: "transparent",
                                controlHeight: 40,
                                borderRadius: 8,
                              },
                            }}
                          >
                            <TimePicker
                              value={
                                newRuleData.evenEndTime
                                  ? dayjs(newRuleData.evenEndTime, "hh:mm A")
                                  : null
                              }
                              onChange={(time, timeString) => {
                                handleNewRuleChange("evenEndTime", timeString);
                              }}
                              format="hh:mm A"
                              use12Hours
                              minuteStep={5}
                              showNow={false}
                              variant="borderless"
                              className="w-full cursor-pointer"
                              style={{
                                background: "transparent",
                                color: colors.text,
                              }}
                              placeholder="Select end time"
                            />
                          </ConfigProvider>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Odd Addresses */}
                  <div
                    className="rounded-xl border p-6"
                    style={{
                      backgroundColor: colors.cardBg,
                      borderColor: colors.border,
                    }}
                  >
                    <div className="mb-5 flex items-center justify-between gap-3">
                      <h3
                        className="text-sm font-extrabold uppercase tracking-wide"
                        style={{ color: colors.primary }}
                      >
                        Odd Addresses
                      </h3>

                      <p className="text-xs font-medium text-sky-100/80">
                        Ends in 1, 3, 5, 7, 9
                      </p>
                    </div>

                    <label className={labelClass}>Permitted Days</label>

                    <div className="mb-5 flex flex-wrap gap-2">
                      {[
                        { key: "M", label: "M" },
                        { key: "T", label: "T" },
                        { key: "W", label: "W" },
                        { key: "T2", label: "T" },
                        { key: "F", label: "F" },
                        { key: "S", label: "S" },
                        { key: "S2", label: "S" },
                      ].map((day) => {
                        const isActive = newRuleData.oddDays.includes(day.key);

                        return (
                          <button
                            key={day.key}
                            type="button"
                            onClick={() => toggleParityDay("odd", day.key)}
                            className="h-9 w-9 rounded-md border text-xs font-extrabold transition-all"
                            style={{
                              backgroundColor: isActive
                                ? colors.primary
                                : "transparent",
                              borderColor: isActive
                                ? colors.primary
                                : colors.border,
                              color: isActive
                                ? colors.buttonText || "#061727"
                                : colors.text,
                            }}
                          >
                            {day.label}
                          </button>
                        );
                      })}
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className={labelClass}>Start Time</label>

                        <div
                          className="flex h-[48px] items-center rounded-lg border px-3"
                          style={inputStyle}
                        >
                          <ConfigProvider
                            theme={{
                              algorithm: isLightTheme
                                ? antTheme.defaultAlgorithm
                                : antTheme.darkAlgorithm,
                              token: {
                                colorPrimary: colors.primary,
                                colorBgContainer: colors.background,
                                colorText: colors.text,
                                colorTextPlaceholder: colors.textSecondary,
                                colorBorder: "transparent",
                                controlHeight: 40,
                                borderRadius: 8,
                              },
                            }}
                          >
                            <TimePicker
                              value={
                                newRuleData.oddStartTime
                                  ? dayjs(newRuleData.oddStartTime, "hh:mm A")
                                  : null
                              }
                              onChange={(time, timeString) => {
                                handleNewRuleChange("oddStartTime", timeString);
                              }}
                              format="hh:mm A"
                              use12Hours
                              minuteStep={5}
                              showNow={false}
                              variant="borderless"
                              className="w-full cursor-pointer"
                              style={{
                                background: "transparent",
                                color: colors.text,
                              }}
                              placeholder="Select start time"
                            />
                          </ConfigProvider>
                        </div>
                      </div>

                      <div>
                        <label className={labelClass}>End Time</label>

                        <div
                          className="flex h-[48px] items-center rounded-lg border px-3"
                          style={inputStyle}
                        >
                          <ConfigProvider
                            theme={{
                              algorithm: isLightTheme
                                ? antTheme.defaultAlgorithm
                                : antTheme.darkAlgorithm,
                              token: {
                                colorPrimary: colors.primary,
                                colorBgContainer: colors.background,
                                colorText: colors.text,
                                colorTextPlaceholder: colors.textSecondary,
                                colorBorder: "transparent",
                                controlHeight: 40,
                                borderRadius: 8,
                              },
                            }}
                          >
                            <TimePicker
                              value={
                                newRuleData.oddEndTime
                                  ? dayjs(newRuleData.oddEndTime, "hh:mm A")
                                  : null
                              }
                              onChange={(time, timeString) => {
                                handleNewRuleChange("oddEndTime", timeString);
                              }}
                              format="hh:mm A"
                              use12Hours
                              minuteStep={5}
                              showNow={false}
                              variant="borderless"
                              className="w-full cursor-pointer"
                              style={{
                                background: "transparent",
                                color: colors.text,
                              }}
                              placeholder="Select end time"
                            />
                          </ConfigProvider>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className="flex flex-col gap-3 border-t px-6 py-5 sm:flex-row sm:justify-end"
              style={{ borderColor: colors.border }}
            >
              <button
                type="button"
                onClick={() => setIsAddRuleModalOpen(false)}
                className="inline-flex items-center justify-center rounded-lg border px-5 py-3 text-sm font-bold transition-all hover:opacity-90"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                  color: colors.text,
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => {
                  console.log("New Rule Data:", newRuleData);
                  setIsAddRuleModalOpen(false);
                }}
                className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-extrabold transition-all hover:opacity-90"
                style={{
                  backgroundColor: colors.primary,
                  color: colors.buttonText || "#061727",
                }}
              >
                <Save size={17} />
                Save Rule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RuleConfigurations;
